import React from "react";
import { Role } from "../../types";

interface NavItem {
  id: string;
  label: string;
  icon: string;
  section?: string;
}

const NAV: Record<Role, NavItem[]> = {
  super_admin: [
    { id: "dashboard",     label: "Dashboard",     icon: "⚡" },
    { id: "organizations", label: "Organizations", icon: "🏢",  section: "Management" },
    { id: "users",         label: "Users & Roles", icon: "👥" },
    { id: "scale",         label: "Scale Master",  icon: "⚖️",  section: "Assessment" },
    { id: "trait-categories", label: "Trait Categories", icon: "📝" },
    { id: "traits",        label: "Traits",        icon: "🧠" },
    { id: "question-type", label: "Question Types", icon: "📝" },
    { id: "sections",      label: "Sections",      icon: "📂" },
    { id: "questions",     label: "Question Bank", icon: "❓" },
    { id: "scoring_rules", label: "Scoring Rules", icon: "🎯" },
    { id: "manage_assessment_templates",         label: "Manage Assessment",         icon: "📋" },
    // { id: "assignments",   label: "Assignments",   icon: "📌" },
    { id: "results",       label: "Results",       icon: "📊",  section: "Analytics" },
    { id: "reports",       label: "Reports",       icon: "📄" },
    { id: "settings",      label: "Settings",      icon: "⚙️",  section: "System"    },
  ],
  org_admin: [
    { id: "dashboard",   label: "Dashboard",     icon: "⚡" },
    { id: "candidates",  label: "Candidates",    icon: "👤",  section: "Management" },
    { id: "assessment", label: "Create Exam",  icon: "📋" },
    { id: "assignments", label: "Assign Exams",  icon: "📌" },
    { id: "results",     label: "Results",       icon: "📊",  section: "Analytics" },
    { id: "reports",     label: "Reports",       icon: "📄" },
  ],
  candidate: [
    { id: "dashboard", label: "My Dashboard", icon: "⚡" },
    { id: "exams",     label: "My Exams",     icon: "📋" },
    { id: "results",   label: "My Results",   icon: "📊" },
    { id: "reports",   label: "My Reports",   icon: "📄" },
  ],
};

interface Props {
  role: Role;
  activeTab: string;
  onTabChange: (tab: string) => void;
  collapsed: boolean;
  onCollapse: () => void;
}

const Sidebar: React.FC<Props> = ({ role, activeTab, onTabChange, collapsed, onCollapse }) => {
  const items = NAV[role] ?? [];
  let lastSection = "";

  return (
    <aside
      className={`fixed top-0 left-0 h-screen z-40 flex flex-col transition-all duration-300 ${collapsed ? "w-16" : "w-64"}`}
      style={{
        background: "rgba(255,255,255,0.72)",
        backdropFilter: "blur(22px)",
        WebkitBackdropFilter: "blur(22px)",
        borderRight: "1px solid rgba(255,255,255,0.35)",
        boxShadow: "4px 0 28px rgba(99,102,241,0.07)",
      }}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-white/30 ${collapsed ? "justify-center" : ""}`}>
        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-extrabold text-sm flex-shrink-0 shadow-md">
          PS
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="font-extrabold text-gray-800 text-sm leading-tight tracking-tight">PsychoAssess</p>
            <p className="text-xs text-gray-400 font-medium">SaaS Platform</p>
          </div>
        )}
        <button
          onClick={onCollapse}
          className={`${collapsed ? "hidden" : ""} ml-auto w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 text-xs transition-colors`}
        >
          ◀
        </button>
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <button
          onClick={onCollapse}
          className="mx-auto mt-2 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 text-xs transition-colors"
        >▶</button>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {items.map(item => {
          const showSection = item.section && item.section !== lastSection;
          if (item.section) lastSection = item.section;

          return (
            <React.Fragment key={item.id}>
              {showSection && !collapsed && (
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 pt-5 pb-1">
                  {item.section}
                </p>
              )}
              <button
                onClick={() => onTabChange(item.id)}
                title={collapsed ? item.label : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150
                  ${activeTab === item.id
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                    : "text-gray-600 hover:bg-white/80 hover:text-gray-900"
                  }
                  ${collapsed ? "justify-center" : ""}`}
              >
                <span className="text-base flex-shrink-0">{item.icon}</span>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Role badge */}
      {!collapsed && (
        <div className="px-4 py-4 border-t border-white/30">
          <div className="px-3 py-2 rounded-xl bg-indigo-50 border border-indigo-100">
            <p className="text-xs font-bold text-indigo-600 capitalize">{role.replace("_", " ")}</p>
            <p className="text-[10px] text-indigo-400 mt-0.5">Active Session</p>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
